import React, { useEffect, useState } from "react";
import {
  Card,
  Spinner,
  Alert,
  Container,
  Row,
  Col,
  Button,
  Collapse,
  Modal,
} from "react-bootstrap";
import CreateReportModal from "../components/Reports/CreateReportModal";
import DeleteReportButton from "../components/Reports/DeleteReportButton";
import { Form, InputGroup } from "react-bootstrap";
import debounce from "lodash.debounce";

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  const [expandedProjects, setExpandedProjects] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const fetchSuggestions = debounce(async (term) => {
    if (!term) return setSuggestions([]);

    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(
        `https://loopsneller.com/api/reports/suggestions?term=${term}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const data = await res.json();
      console.log("Suggestions API returned:", data);

      const types = data.types || [];
      const projects = data.projects || [];
      const users = data.users || [];

      const combined = [...types, ...projects, ...users];
      setSuggestions(combined.slice(0, 5));
    } catch (err) {
      console.error("Failed to fetch suggestions", err);
    }
  }, 300);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchSuggestions(value);
  };

  const handleSearchSubmit = async (e) => {
    if (e) e.preventDefault();
    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(
        `https://loopsneller.com/api/reports?search=${searchTerm}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      const data = await res.json();
      setReports(data);
    } catch (err) {
      console.error("Search failed", err);
    }
  };

  const fetchReports = () => {
    const token = localStorage.getItem("auth_token");

    fetch("https://loopsneller.com/api/reports", {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch reports");
        return res.json();
      })
      .then((data) => {
        const sorted = data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setReports(sorted);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load reports.");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleViewDetails = (reportId) => {
    const token = localStorage.getItem("auth_token");
    setModalLoading(true);
    setModalError(null);
    setShowModal(true);

    fetch(`https://loopsneller.com/api/reports/${reportId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch report details");
        return res.json();
      })
      .then((data) => {
        setSelectedReport(data);
        setModalLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setModalError("Could not load report details.");
        setModalLoading(false);
      });
  };

  const handleNewReport = (newReport) => {
    setReports((prev) => [newReport, ...prev]);
  };

  // Group reports by project ID
  const groupedReports = reports.reduce((acc, report) => {
    const projectName = report.project?.name || "No Project";
    if (!acc[projectName]) acc[projectName] = [];
    acc[projectName].push(report);
    return acc;
  }, {});

  const toggleExpand = (projectName) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectName]: !prev[projectName],
    }));
  };

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-2 w-75">
          <div className="position-relative w-50">
            <Form.Control
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchSubmit(e);
                }
              }}
            />
            {suggestions.length > 0 && (
              <div
                className="position-absolute bg-white border rounded shadow-sm"
                style={{ top: "100%", zIndex: 1000, width: "100%" }}
              >
                {suggestions.map((sug, index) => (
                  <div
                    key={index}
                    className="p-2 hover-bg-light cursor-pointer"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSearchTerm(sug);
                      setSuggestions([]);
                      handleSearchSubmit(); // trigger search on click
                    }}
                  >
                    {sug}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button
            variant="primary"
            size="sm"
            onClick={handleSearchSubmit}
            className="mx-2"
          >
            Search
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setSearchTerm("");
              setSuggestions([]);
              fetchReports();
            }}
          >
            Clear
          </Button>
        </div>

        <div className="ms-3">
          <CreateReportModal onReportCreated={handleNewReport} />
        </div>
      </div>

      {loading && <Spinner animation="border" />}
      {error && <Alert variant="danger">{error}</Alert>}
      {!loading && reports.length === 0 && (
        <Alert variant="info">You haven’t created any reports yet.</Alert>
      )}

      {Object.entries(groupedReports).map(([projectName, projectReports]) => {
        const isExpanded = expandedProjects[projectName];
        const visibleReports = isExpanded
          ? projectReports
          : projectReports.slice(0, 6);

        return (
          <Card key={projectName} className="mb-4 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{projectName}</h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => toggleExpand(projectName)}
              >
                {isExpanded ? "Show Less" : "Show More"}
              </Button>
            </Card.Header>
            <Card.Body>
              <Row>
                {visibleReports.map((report) => (
                  <Col md={6} lg={4} key={report.id} className="mb-3">
                    <Card className="h-100 shadow-sm">
                      <Card.Body>
                        <Card.Title>{report.type}</Card.Title>
                        <Card.Subtitle className="mb-2 text-muted">
                          Created:{" "}
                          {new Date(report.created_at).toLocaleString()}
                        </Card.Subtitle>
                        <Card.Text>
                          <strong>By:</strong> {report.user?.name || "Unknown"}
                        </Card.Text>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewDetails(report.id)}
                        >
                          View Details
                        </Button>

                        <DeleteReportButton
                          reportId={report.id}
                          onDelete={(deletedId) =>
                            setReports((prev) =>
                              prev.filter((r) => r.id !== deletedId)
                            )
                          }
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        );
      })}

      {/* MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Report Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading && <Spinner animation="border" />}
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          {!modalLoading && selectedReport && (
            <>
              <p>
                <strong>Type:</strong> {selectedReport.type}
              </p>
              <p>
                <strong>Project:</strong>{" "}
                {selectedReport.project?.name || "No Project"}
              </p>
              <p>
                <strong>Created by:</strong>{" "}
                {selectedReport.user?.name || "Unknown"}
              </p>
              <p>
                <strong>Created at:</strong>{" "}
                {new Date(selectedReport.created_at).toLocaleString()}
              </p>
              <p>
                <p>
                  <strong>Data:</strong>
                </p>
                <div className="bg-light p-3 rounded">
                  <p>
                    <strong>Summary:</strong>{" "}
                    {selectedReport.data?.summary || "N/A"}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {selectedReport.data?.status || "N/A"}
                  </p>
                  <p>
                    <strong>Notes:</strong>{" "}
                    {selectedReport.data?.notes || "N/A"}
                  </p>
                </div>
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ReportsPage;
